import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js/pure';
import { customerPortalAPI } from '../../services/api';
import {
  invoiceReplacementAPI as api,
  PublicReplacementView,
  ReplacementPaymentReceipt,
  REPLACEMENT_CONSENT_VERSION,
  replacementMoney,
} from '../../services/invoiceReplacementAPI';

const button = 'rounded-md border border-outline px-4 py-2 disabled:opacity-50';
function portalKey() {
  return `modal_customer_portal:${window.location.host}`;
}
function readPortalToken() {
  try {
    const session = JSON.parse(localStorage.getItem(portalKey()) || 'null');
    if (
      typeof session?.portalToken !== 'string' ||
      Date.parse(session.expiresAt) <= Date.now() ||
      !Number.isFinite(Date.parse(session.expiresAt))
    )
      return '';
    return session.portalToken;
  } catch {
    return '';
  }
}
function attemptKey(itemId: string) {
  return `modal_replacement_attempt:${window.location.host}:${itemId}`;
}

export default function ReplacementPaymentPage() {
  const { token = '' } = useParams();
  return <ReplacementPayment token={token} key={token} />;
}

function ReplacementPayment({ token }: { token: string }) {
  const [portalToken, setPortalToken] = useState(readPortalToken);
  const [email, setEmail] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [code, setCode] = useState('');
  const [view, setView] = useState<PublicReplacementView | null>(null);
  const [receipt, setReceipt] = useState<ReplacementPaymentReceipt | null>(
    null
  );
  const [consent, setConsent] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const lock = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const failure = useCallback((error: unknown, hasInvoice = false) => {
    const status =
      (error as { status?: number; response?: { status?: number } })?.status ||
      (error as { response?: { status?: number } })?.response?.status;
    if (status === 401 || status === 403) {
      localStorage.removeItem(portalKey());
      setPortalToken('');
      setView(null);
      setReceipt(null);
      setConsent(false);
      setMessage(
        'Customer authentication expired or does not match this invoice. Verify the correct customer email to continue.'
      );
    } else if (status === 404 || status === 410) {
      setView(null);
      setMessage(
        'This replacement link is invalid or expired. Request a new link from the operator.'
      );
    } else if (!hasInvoice) {
      setMessage(
        'Could not verify customer access or load this invoice. Check your code or request a new link, then try reading again. No payment was attempted.'
      );
    } else
      setMessage(
        'Outcome unknown. Check payment status before taking any further action. Your payment will not be retried automatically.'
      );
  }, []);

  useEffect(() => {
    if (!portalToken || !token) return;
    let active = true;
    setBusy(true);
    lock.current = true;
    (async () => {
      try {
        const next = await api.publicGet(token, portalToken);
        if (!active) return;
        setView(next);
        setConsent(false);
        const pending =
          sessionStorage.getItem(attemptKey(next.itemId)) === 'pending';
        setAttempted(pending);
        if (
          pending ||
          ['paid', 'processing', 'requires_action', 'unknown'].includes(
            next.status
          )
        ) {
          const status = await api.status(token, portalToken);
          if (active) setReceipt(status);
        }
      } catch (error) {
        if (active) failure(error);
      } finally {
        if (active) {
          setBusy(false);
          lock.current = false;
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [failure, portalToken, token]);

  async function run(action: () => Promise<void>) {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setMessage('');
    try {
      await action();
    } catch (error) {
      if (mounted.current) failure(error, !!view);
    } finally {
      if (mounted.current) {
        setBusy(false);
        lock.current = false;
      }
    }
  }
  async function checkStatus() {
    const status = await api.status(token, portalToken);
    setReceipt(status);
    setMessage(
      status.status === 'paid'
        ? 'Payment verified by the provider receipt.'
        : 'Provider status checked. No new payment attempt was made.'
    );
  }
  async function authenticatePayment(result: ReplacementPaymentReceipt) {
    setReceipt(result);
    if (result.status === 'unknown')
      setMessage(
        'Outcome unknown. Check payment status. Your existing payment will not be retried automatically.'
      );
    if (result.status !== 'requires_action' || !result.clientSecret || !view)
      return;
    const stripe = await loadStripe(view.publishableKey, {
      stripeAccount: view.stripeAccountId,
    });
    if (!stripe) throw new Error('Payment verification unavailable.');
    const action = await stripe.handleNextAction({
      clientSecret: result.clientSecret,
    });
    // The browser's result is not a receipt; always verify it on the server.
    await checkStatus();
    if (action.error)
      setMessage(
        'Card authentication did not complete. Check payment status before continuing.'
      );
  }
  const payable =
    !!view &&
    view.status === 'ready' &&
    (!receipt || receipt.status === 'ready') &&
    !attempted &&
    view.consentVersion === REPLACEMENT_CONSENT_VERSION;
  const status = receipt?.status || view?.status;

  return (
    <main className="min-h-screen bg-surface px-4 py-10 text-on-surface">
      <div className="mx-auto max-w-xl space-y-5 rounded-lg border border-outline bg-surface-container-lowest p-6">
        <h1 className="text-2xl font-semibold">Replacement invoice payment</h1>
        <div role="status" aria-live="polite" className="min-h-[1.5rem]">
          {busy ? 'Please wait…' : message}
        </div>
        {!portalToken && (
          <section aria-label="Customer email verification">
            <p>
              Verify your customer email before viewing or paying this invoice.
            </p>
            {!challengeId ? (
              <form
                className="space-y-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (email.trim())
                    void run(async () => {
                      const response = await customerPortalAPI.startOtp(
                        email.trim()
                      );
                      setChallengeId(response.data.challengeId || '');
                      setMessage('Check your email for the verification code.');
                    });
                }}
              >
                <label className="block">
                  Customer email
                  <input
                    className="block w-full rounded border border-outline bg-surface p-2"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    disabled={busy}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>
                <button className={button} disabled={busy || !email.trim()}>
                  Send verification code
                </button>
              </form>
            ) : (
              <form
                className="space-y-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!/^\d{6}$/.test(code)) return;
                  void run(async () => {
                    const response = await customerPortalAPI.verifyOtp({
                      challengeId,
                      code,
                    });
                    const session = response.data;
                    if (
                      typeof session.portalToken !== 'string' ||
                      !Number.isFinite(Date.parse(session.expiresAt)) ||
                      Date.parse(session.expiresAt) <= Date.now()
                    )
                      throw new Error('Customer session unavailable.');
                    localStorage.setItem(
                      portalKey(),
                      JSON.stringify({
                        portalToken: session.portalToken,
                        email: session.email,
                        expiresAt: session.expiresAt,
                      })
                    );
                    setPortalToken(session.portalToken);
                    setConsent(false);
                    setMessage('Customer email verified.');
                  });
                }}
              >
                <label className="block">
                  Verification code
                  <input
                    className="block w-full rounded border border-outline bg-surface p-2"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    required
                    maxLength={6}
                    value={code}
                    disabled={busy}
                    onChange={(e) =>
                      setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                  />
                </label>
                <button className={button} disabled={busy || code.length !== 6}>
                  Verify email
                </button>
                <button
                  className={button}
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setChallengeId('');
                    setCode('');
                  }}
                >
                  Use another email
                </button>
              </form>
            )}
          </section>
        )}
        {view && portalToken && (
          <section
            className="space-y-4"
            aria-label="Invoice payment confirmation"
          >
            <h2 className="text-lg font-semibold">
              Invoice {view.invoiceNumber}
            </h2>
            <p>Replaces original {view.originalNumber}</p>
            <p className="text-xl font-semibold">
              {replacementMoney(view.amount, view.currency)}
            </p>
            <p>
              Saved card: {view.card.brand} ending {view.card.last4}
            </p>
            <p>Payment status: {status}</p>
            {status === 'paid' && (
              <p>Payment received. No further payment is required.</p>
            )}
            {(attempted ||
              ['processing', 'requires_action', 'unknown'].includes(
                status || ''
              )) &&
              status !== 'paid' && (
                <p>
                  Your existing payment attempt is preserved. Check its receipt;
                  do not submit another payment.
                </p>
              )}
            {status === 'requires_payment_method' && (
              <p>
                The saved card could not complete payment. Contact the operator
                for assistance.
              </p>
            )}
            {view.consentVersion !== REPLACEMENT_CONSENT_VERSION && (
              <p>
                This payment confirmation has changed. Request an updated
                payment link.
              </p>
            )}
            <form
              className="space-y-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (!consent || !payable) return;
                void run(async () => {
                  // Persist before sending: a refresh or lost response must never silently submit again.
                  sessionStorage.setItem(attemptKey(view.itemId), 'pending');
                  setAttempted(true);
                  const result = await api.confirm(token, portalToken, {
                    amount: view.amount,
                    currency: view.currency,
                    consentVersion: view.consentVersion,
                  });
                  await authenticatePayment(result);
                });
              }}
            >
              <label className="block">
                <input
                  type="checkbox"
                  checked={consent}
                  disabled={busy || !payable}
                  onChange={(event) => setConsent(event.target.checked)}
                />{' '}
                I authorize a one-time payment of{' '}
                {replacementMoney(view.amount, view.currency)} for invoice{' '}
                {view.invoiceNumber} using my saved {view.card.brand} card
                ending {view.card.last4}.
              </label>
              <button
                className={button}
                disabled={busy || !consent || !payable}
              >
                Pay {replacementMoney(view.amount, view.currency)}
              </button>
            </form>
            <button
              className={button}
              disabled={
                busy ||
                (!attempted &&
                  (!receipt || receipt.status === 'ready') &&
                  view.status === 'ready')
              }
              onClick={() => run(checkStatus)}
            >
              Check payment status
            </button>
            {receipt?.status === 'requires_action' && receipt.clientSecret && (
              <button
                className={button}
                disabled={busy}
                onClick={() => run(() => authenticatePayment(receipt))}
              >
                Continue card authentication
              </button>
            )}
          </section>
        )}
      </div>
    </main>
  );
}
